import { Button } from '@/components/ui/button';
import { Minus, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalHeaderProps {
  title: string;
  onMinimize?: () => void;
  onClose: () => void;
  className?: string;
}

/**
 * Simplified ModalHeader component for modals that can be minimized to the taskbar.
 * 
 * This is a presentational component - all window management logic is handled
 * by the parent component (page) that owns the modal state.
 * 
 * Props:
 * - onMinimize: Called when minimize button is clicked. Parent should register
 *   window with WindowManager and close the dialog.
 * - onClose: Called when close button is clicked. Parent should fully close
 *   the dialog and clean up any WindowManager registration.
 */
export const ModalHeader = ({
  title,
  onMinimize,
  onClose,
  className,
}: ModalHeaderProps) => {
  return (
    <div className={cn("flex items-center justify-between pb-4 border-b border-border", className)}>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <div className="flex items-center gap-1">
        {onMinimize && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMinimize();
            }}
            title="Minimize to taskbar"
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-md hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          title="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
